import { createContext, useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { jwtDecode } from 'jwt-decode';
import {
    authAPI,
    performNuclearLogout,
    refreshAccessToken as refreshAccessTokenRequest,
    registerAuthStateHandlers,
} from '../services/api';
import {
    clearStoredAuthTokens,
    getStoredTokenForAudience,
    setStoredRefreshToken,
    setStoredTokenForRole,
} from '../utils/authStorage';

function safeDecode(token) {
    try {
        return jwtDecode(token);
    } catch (e) {
        console.error('JWT Decode Error:', e);
        return null;
    }
}

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    // 2026-07-25 C3 auth 翻新：初值改回 null（不能像過去 localStorage 那樣同步塞進 useState
    // 初始化函式），因為 @capacitor/preferences 是非同步 API（spec 4.1）。真正的搬遷 + 讀取
    // 搬進下面的 mount effect 內執行；既有的 loading state（本來就有、預設 true）延續扮演
    // 「還沒讀完 storage 前顯示 LoadingScreen」的角色，PrivateRoute.jsx 已經在依賴這個 flag，
    // 架構上完全吃得下這個改動、不會多一次畫面閃爍。
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);

    // Mount 時執行一次：(a) 註冊 refresh 成功回呼，讓 api.js 的攔截器／resume 檢查不論誰觸發
    // 了 refresh，成功後都會同步更新這裡的 token state（spec 4.5 第 1 點——WebSocketContext
    // 的 useEffect 依賴 [token, user]，token state 更新後會自動 cleanup 舊連線並用新 token
    // 重連，不需要另外手動處理 WS 重連）；(b) 做 spec 4.2 的搬遷 + 讀取。
    useEffect(() => {
        registerAuthStateHandlers({
            onTokenRefreshed: (newAccessToken) => setToken(newAccessToken),
        });

        let cancelled = false;
        (async () => {
            const stored = await getStoredTokenForAudience();
            if (!cancelled) setToken(stored || '');
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        // token 仍是初始值 null，代表上面的搬遷/讀取尚未完成，先不要提前判斷「未登入」
        // （否則會有一瞬間 loading=false 但其實還沒讀完 storage 的錯誤畫面）。
        if (token === null) return undefined;

        let cancelled = false;

        const initAuth = async () => {
            if (token) {
                const decoded = safeDecode(token);
                if (decoded) {
                    // 樂觀先用 JWT payload 湊出臨時 user，讓畫面不用等 getMe() 網路來回。
                    // 刻意不在這裡檢查 exp：即使 access token 名目上已過期也直接呼叫 getMe()，
                    // 過期會被下面 api.js 的攔截器攔到 401 並靜默 refresh+重試，這裡完全無感知
                    // 地收到結果（spec 6.1「access token 自然過期後下一次 API 呼叫」那一列，
                    // App 啟動時的 getMe() 本身就是「下一次 API 呼叫」，不需要另外重造一套邏輯）。
                    // 只有 refresh 也失敗（含舊版單 token 使用者沒有 refresh token 可用）時，
                    // 攔截器才會 nuclear 登出，這裡的 catch 只需要不重複處理即可。
                    setUser({
                        phoneNumber: decoded.sub,
                        role: decoded.role,
                        id: decoded.user_id,
                    });

                    try {
                        const res = await authAPI.getMe();
                        if (!cancelled) setUser((prev) => ({ ...prev, ...res.data }));
                    } catch (err) {
                        console.warn('Failed to fetch user details:', err);
                    }
                } else {
                    // token 結構上就不是合法 JWT（例如毀損字串），無法靠打 API 讓後端判斷，
                    // 直接走本地登出。
                    await logout();
                }
            }

            if (!cancelled) setLoading(false);
        };

        initAuth();
        return () => {
            cancelled = true;
        };
    }, [token]);

    // spec 4.4：比照 NotificationContext.jsx:147-159／AnnouncementBanner.jsx:92-99 既有的
    // visibilitychange pattern。App 回前景時檢查目前 access token 剩餘壽命，若已過期或剩不到
    // 2 分鐘，主動觸發 refresh，不要被動等下一次 API 呼叫 401 才發現（避免「手機放口袋幾小時
    // 後拿出來用」時先出現一次可感知的失敗請求）。
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible' || !token) return;

            const decoded = safeDecode(token);
            if (!decoded?.exp) return;

            const secondsRemaining = decoded.exp - Date.now() / 1000;
            if (secondsRemaining < 120) {
                console.log('App foregrounded, access token expired/near expiry, refreshing...');
                refreshAccessTokenRequest().catch((err) => {
                    // 與攔截器不同，這裡是「主動」提前刷新，失敗不代表使用者馬上有感知
                    // （還沒有任何請求真的被拒絕）。真正的 nuclear 登出仍統一交給
                    // performNuclearLogout：resume 時就發現 refresh 徹底失效的話，
                    // 直接在這裡導頁，不用等使用者自己再觸發一次 401 才處理。
                    console.warn('Resume-time refresh failed:', err.message);
                    performNuclearLogout();
                });
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [token]);

    const login = async (phoneNumber, password) => {
        try {
            const response = await authAPI.login(phoneNumber, password);
            const { access_token, refresh_token } = response.data;
            const decoded = safeDecode(access_token);

            if (!decoded?.role) {
                throw new Error('Invalid access token payload');
            }

            await setStoredTokenForRole(access_token);
            await setStoredRefreshToken(refresh_token);
            setToken(access_token);
            setUser({
                phoneNumber: decoded.sub,
                role: decoded.role,
                id: decoded.user_id,
            });

            return { success: true };
        } catch (error) {
            console.error('Login failed:', error);

            let errMsg = '登入失敗，請檢查帳號密碼';

            if (error.response) {
                errMsg = error.response.data?.detail || errMsg;
            } else if (error.code === 'ECONNABORTED') {
                errMsg = '連線逾時，請檢查網路狀況';
            } else if (error.request) {
                errMsg = '無法連接至伺服器，請檢查網路連線或防火牆設定';
            } else {
                errMsg = error.message;
            }

            return {
                success: false,
                message: errMsg,
            };
        }
    };

    const logout = async () => {
        const fcmToken = localStorage.getItem('fcm_token');
        if (fcmToken) {
            try {
                await authAPI.unregisterDevice(fcmToken);
                console.log('Device unregistered successfully');
            } catch (err) {
                console.warn('Failed to unregister device:', err);
            }
            localStorage.removeItem('fcm_token');
        }

        setToken('');
        setUser(null);
        await clearStoredAuthTokens();
    };

    const hasRole = (allowedRoles) => {
        if (!user) return false;
        return allowedRoles.includes(user.role);
    };

    return (
        <AuthContext.Provider
            value={{ user, token, login, logout, hasRole, loading, refreshAccessToken: refreshAccessTokenRequest }}
        >
            {children}
        </AuthContext.Provider>
    );
};

AuthProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useAuth = () => useContext(AuthContext);
